function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import { InlineSelect, KeyboardDatePicker, MenuItem, Tooltip, Typography } from 'seagull';
import { daysOfWeek, defaultBillingDayOfWeek, getDefaultBillingDayOfYear } from "../../../constants";
import { useStoreActions } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { ordinal } from "../../../infra/utils";
import { BillingDayOfMonthModel, serviceBillingModel, serviceModel } from "../../../models";
import { validateBillingDayOfMonth } from "../../../models/service";
import { RecurrencePeriodTypes } from "../../../models/service-billing";
import { InlineUpfrontSelect } from "../BillingInitiationControl/InlineUpfrontSelect/InlineUpfrontSelect";
import styles from "./BillingSettingsSection.module.scss";
import { InlineStartBillingControl } from "./InlineStartBillingControl/InlineStartBillingControl";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function getDayOfMonthOptions(isBilledUpfront) {
  return new Array(28).fill(null).map((_, idx) => ({
    value: (idx + 1).toString(),
    display: ordinal(idx + 1),
    disabled: false
  })).concat({
    value: 'last',
    display: 'last day',
    disabled: isBilledUpfront
  });
}
function BillingDayOfMonthField(_ref) {
  let {
    service,
    billing,
    dayOfMonthOptions,
    isReadOnly,
    disableBillingDay,
    disabled,
    testid,
    showError,
    isActiveAgreement,
    Text,
    onHasError,
    onDayOfMonthChange,
    setShowBillingDayOfMonthError
  } = _ref;
  const {
    logBI
  } = useStoreActions();
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const {
    billingDayOfMonth
  } = billing;
  const value = billingDayOfMonth ? billingDayOfMonth.toNormalizedDayOfMonth().toString() : 'last';
  const readOnlyValue = useMemo(() => {
    var _dayOfMonthOptions$fi;
    return ((_dayOfMonthOptions$fi = dayOfMonthOptions.find(opt => opt.value === value)) === null || _dayOfMonthOptions$fi === void 0 ? void 0 : _dayOfMonthOptions$fi.display) || '';
  }, [dayOfMonthOptions, value]);
  return isReadOnly ? /*#__PURE__*/_jsx(Text, {
    children: readOnlyValue
  }) : /*#__PURE__*/_jsx(InlineSelect, {
    skin: "primary",
    disabled: disableBillingDay || disabled,
    value: value,
    testid: "".concat(testid, "billing-day-dropdown"),
    error: showError,
    onChange: e => {
      const day = e.target.value;
      logBI(11014, {
        isActiveAgreement,
        serviceId: service.id,
        day
      });
      const isLast = day === dayOfMonthOptions[dayOfMonthOptions.length - 1].value;
      const newBillingDayOfMonth = BillingDayOfMonthModel.make(isLast ? 'last' : +day);
      const hasError = validateBillingDayOfMonth(newBillingDayOfMonth, isBilledUpfront);
      setShowBillingDayOfMonthError(hasError);
      onHasError && onHasError(service.id, hasError);
      onDayOfMonthChange(service.id, newBillingDayOfMonth);
    },
    children: dayOfMonthOptions.map((_ref2, idx) => {
      let {
        value,
        disabled,
        display
      } = _ref2;
      return /*#__PURE__*/_jsx(MenuItem, {
        value: value,
        disabled: disabled,
        "data-testid": "".concat(testid, "billing-day-option-").concat(idx),
        children: display
      }, value);
    })
  });
}
function BillingDayOfWeekField(_ref3) {
  let {
    service,
    disableBillingDay,
    disabled,
    testid,
    isActiveAgreement,
    isReadOnly,
    Text,
    onDayOfWeekChange
  } = _ref3;
  const {
    logBI
  } = useStoreActions();
  const value = serviceBillingModel.billingDayOfWeek(service.billing) || defaultBillingDayOfWeek;
  const readOnlyValue = useMemo(() => daysOfWeek.find(day => day === value), [value]);
  return isReadOnly ? /*#__PURE__*/_jsx(Text, {
    children: readOnlyValue
  }) : /*#__PURE__*/_jsx(InlineSelect, {
    skin: "primary",
    disabled: disableBillingDay || disabled,
    value: value,
    testid: "".concat(testid, "billing-day-dropdown"),
    onChange: e => {
      logBI(11015, {
        isActiveAgreement,
        serviceId: service.id,
        day: e.target.value
      });
      onDayOfWeekChange(service.id, e.target.value);
    },
    children: daysOfWeek.map((option, idx) => /*#__PURE__*/_jsx(MenuItem, {
      value: option,
      "data-testid": "".concat(testid, "billing-day-option-").concat(idx),
      children: option
    }, option))
  });
}
function BillingDayOfYearField(_ref4) {
  let {
    service,
    isReadOnly,
    Text,
    onDayOfYearChange
  } = _ref4;
  const value = useMemo(() => serviceModel.billingDayOfYear(service) || getDefaultBillingDayOfYear(), [service]);
  const readOnlyValue = useMemo(() => format(value, 'MMMM d'), [value]);
  return isReadOnly ? /*#__PURE__*/_jsx(Text, {
    children: readOnlyValue
  }) : /*#__PURE__*/_jsx(KeyboardDatePicker, {
    KeyboardButtonProps: {
      style: {
        padding: 8
      }
    },
    "data-testid": "billing-day-year",
    format: "MMMM d",
    size: "small",
    value: new Date(value),
    onChange: date => {
      if (date) {
        // TODO:ben add BI for this
        onDayOfYearChange(service.id, date.getTime());
      }
    },
    className: styles.datePicker
  });
}
function InnerBillingSettingsSection(props) {
  const {
    service,
    skin,
    className,
    relationshipState,
    isReadOnly,
    disabled,
    testid,
    isFreshlyAdded,
    onPausedChange,
    onBillOnAcceptanceChange,
    onStartBillingAtChange
  } = props;
  const [showBillingDayOfMonthError, setShowBillingDayOfMonthError] = useState(false);
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const billing = serviceModel.billingTrigger(service);
  const dayOfMonthOptions = useMemo(() => getDayOfMonthOptions(isBilledUpfront), [isBilledUpfront]);
  const {
    YEARLY_AUTOMATIC_SERVICE,
    START_BILLING_AT_PHASE_2_INLINE
  } = useFeatureFlags();
  const isYearly = serviceBillingModel.recurrencePeriod(service.billing) === RecurrencePeriodTypes.Yearly;
  const shouldShowYearlyAutomaticError = isYearly && !YEARLY_AUTOMATIC_SERVICE && !serviceBillingModel.isManual(service.billing);
  const Text = textProps => /*#__PURE__*/_jsx(Typography, _objectSpread({
    noWrap: true,
    skin: skin
  }, textProps));
  useEffect(() => {
    setShowBillingDayOfMonthError(serviceModel.isMonthly(service) && validateBillingDayOfMonth(serviceModel.billingDayOfMonth(service), serviceModel.isBilledUpfront(service)));
  }, [service]);
  const renderInlineStartBillingControl = () => {
    if (!START_BILLING_AT_PHASE_2_INLINE || !relationshipState) {
      return null;
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Typography, {
        skin: skin,
        inline: true,
        as: "span",
        children: ",\xA0"
      }), /*#__PURE__*/_jsx(InlineStartBillingControl, {
        service: service,
        testid: testid,
        disabled: isReadOnly || disabled || (relationshipState === null || relationshipState === void 0 ? void 0 : relationshipState.type) === 'approved' && !isFreshlyAdded,
        relationshipState: relationshipState,
        onPausedChange: onPausedChange,
        onBillOnAcceptanceChange: onBillOnAcceptanceChange,
        onStartBillingAtChange: onStartBillingAtChange
      })]
    });
  };
  const renderOngoingBillingSettings = billing => /*#__PURE__*/_jsxs("div", {
    className: cn(styles.root, className),
    "data-testid": "".concat(props.testid, "-billing-settings-section"),
    children: [/*#__PURE__*/_jsxs(Text, {
      children: ["Billing ", START_BILLING_AT_PHASE_2_INLINE ? 'occurs' : 'is done']
    }), "\xA0", !START_BILLING_AT_PHASE_2_INLINE && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(InlineUpfrontSelect, {
        service: service,
        testid: testid,
        disabled: props.disableBillUpfront || disabled,
        isReadOnly: isReadOnly,
        isActiveAgreement: props.isActiveAgreement,
        TextComponent: Text,
        onChange: props.onBillUpfrontModeChange,
        onHasError: props.onHasError,
        onBillingDayOfMonthError: setShowBillingDayOfMonthError
      }), "\xA0"]
    }), (() => {
      switch (serviceBillingModel.period(billing)) {
        case RecurrencePeriodTypes.Monthly:
          return /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Text, {
              children: "on the"
            }), "\xA0", /*#__PURE__*/_jsx(Tooltip, {
              placement: "top",
              title: showBillingDayOfMonthError ? 'It is not possible to bill upfront on the last day of the month. Please change the billing day.' : '',
              children: /*#__PURE__*/_jsx("span", {
                children: /*#__PURE__*/_jsx(BillingDayOfMonthField, _objectSpread(_objectSpread({}, props), {}, {
                  billing: billing,
                  showError: showBillingDayOfMonthError,
                  dayOfMonthOptions: dayOfMonthOptions,
                  setShowBillingDayOfMonthError: setShowBillingDayOfMonthError,
                  Text: Text
                }))
              })
            }), "\xA0", /*#__PURE__*/_jsx(Text, {
              children: "of each month"
            }), renderInlineStartBillingControl()]
          });
        case RecurrencePeriodTypes.Weekly:
        case RecurrencePeriodTypes.BiWeekly:
          return /*#__PURE__*/_jsxs(_Fragment, {
            children: ["\xA0", /*#__PURE__*/_jsx(Text, {
              children: "on"
            }), "\xA0", /*#__PURE__*/_jsx(BillingDayOfWeekField, _objectSpread(_objectSpread({}, props), {}, {
              Text: Text
            })), renderInlineStartBillingControl()]
          });
        case RecurrencePeriodTypes.Yearly:
          if (!YEARLY_AUTOMATIC_SERVICE) return null;
          return /*#__PURE__*/_jsxs(_Fragment, {
            children: ["\xA0", /*#__PURE__*/_jsx(Text, {
              children: "each year on"
            }), "\xA0", /*#__PURE__*/_jsx(BillingDayOfYearField, _objectSpread(_objectSpread({}, props), {}, {
              Text: Text
            })), renderInlineStartBillingControl()]
          });
        default:
          return null;
      }
    })()]
  });
  const ongoingBillingSettings = serviceBillingModel.isOngoing(billing) && !shouldShowYearlyAutomaticError ? renderOngoingBillingSettings(billing) : null;
  return ongoingBillingSettings;
}
export function BillingSettingsSection(props) {
  const {
    billing
  } = props.service;
  if (serviceBillingModel.isOngoing(billing)) {
    return /*#__PURE__*/_jsx(InnerBillingSettingsSection, _objectSpread({}, props));
  } else {
    return null;
  }
}